import { Platform } from './platform';
import { logger } from './logger.js';

/**
 * Универсальный менеджер haptic feedback
 *
 * Приоритет работы:
 * 1. Telegram WebApp HapticFeedback API (если доступен)
 * 2. Web Vibration API (fallback для обычного браузера)
 * 3. Graceful degradation (если ничего не доступно)
 *
 * Это обеспечивает единый интерфейс для вибрации как в Telegram WebApp,
 * так и при использовании сайта в обычном браузере (будущее расширение).
 */
class HapticManager {
  constructor() {
    this.isTouch = 'ontouchstart' in window;
    this.hasVibration = 'vibrate' in navigator;
    this.hasTelegram = Platform.isTelegram() || Platform.isMax();

    logger.log('HapticManager initialized:', {
      isTouch: this.isTouch,
      hasVibration: this.hasVibration,
      hasTelegram: this.hasTelegram
    });
  }

  /**
   * Удар (impact) - легкая вибрация при взаимодействии с элементами
   * @param {string} style - 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'
   */
  impact(style = 'light') {
    // Попытка 1: Telegram/Max WebApp
    if (this.hasTelegram) {
      Platform.hapticFeedback(style);
      logger.log(`Haptic impact (${Platform.getPlatformType()}): ${style}`);
      return;
    }

    // Попытка 2: Web Vibration API
    if (this.hasVibration) {
      const duration = {
        'light': 10,
        'medium': 20,
        'heavy': 30,
        'rigid': 15,
        'soft': 8
      }[style] || 10;

      navigator.vibrate(duration);
      logger.log(`Haptic impact (Web Vibration): ${style} (${duration}ms)`);
      return;
    }

    logger.log('Haptic impact: not supported');
  }

  /**
   * Изменение выбора - очень легкая вибрация при переключении элементов
   */
  selection() {
    // Попытка 1: Telegram/Max WebApp — use light impact as selection proxy
    if (this.hasTelegram) {
      Platform.hapticFeedback('light');
      logger.log(`Haptic selection (${Platform.getPlatformType()})`);
      return;
    }

    // Попытка 2: Web Vibration API
    if (this.hasVibration) {
      navigator.vibrate(5); // Очень короткая вибрация
      logger.log('Haptic selection (Web Vibration): 5ms');
      return;
    }

    logger.log('Haptic selection: not supported');
  }

  /**
   * Уведомление - паттерн вибрации для различных типов событий
   * @param {string} type - 'success' | 'warning' | 'error'
   */
  notification(type = 'success') {
    // Попытка 1: Telegram/Max WebApp
    if (this.hasTelegram) {
      Platform.hapticFeedback(type);
      logger.log(`Haptic notification (${Platform.getPlatformType()}): ${type}`);
      return;
    }

    // Попытка 2: Web Vibration API с паттернами
    if (this.hasVibration) {
      const pattern = {
        'success': [10, 50, 10],          // Двойная короткая вибрация
        'warning': [20, 100, 20],         // Двойная средняя вибрация
        'error': [30, 100, 30, 100, 30]   // Тройная сильная вибрация
      }[type] || [10];

      navigator.vibrate(pattern);
      logger.log(`Haptic notification (Web Vibration): ${type}`, pattern);
      return;
    }

    logger.log('Haptic notification: not supported');
  }

  /**
   * Проверка доступности haptic feedback
   * @returns {boolean}
   */
  isAvailable() {
    return this.hasTelegram || this.hasVibration;
  }
}

// Экспортируем singleton instance
export default new HapticManager();
